import { useEffect, useState } from "react";
import { projectsApis } from "../../../../api/projects";
import { ALL_STATUSES } from "./statusMeta";

// Data layer for the unified Projects hub. Extracted from allProjectsListView;
// keeps the backend's keyset-pagination contract (paginationId + direction P/F/B).
export const useProjectList = (statusTab: string, filteredData: any) => {
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  // Tab is authoritative for status; the drawer's status field only applies on "All".
  const projectStatusParam = () => {
    if (statusTab === "All") {
      return filteredData?.status?.join(",") || ALL_STATUSES.join(",");
    }
    return statusTab;
  };

  const fetchData = async (params: any) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery: search,
      } = params;
      const apiResponse = await projectsApis.getProjectList({
        page: current,
        pageSize: pageSize,
        searchQuery: search,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        projectStatus: projectStatusParam(),
        tagIds: filteredData?.tag?.join(",") || null,
        projectOwnerIds: filteredData?.owner?.join(",") || null,
        projectGroupIds: filteredData?.group?.join(",") || null,
        completionPercentage: filteredData?.completion || null,
      });
      const { data, status } = apiResponse;
      if (status) {
        const { list, count } = data;
        setPagination((prev) => ({ ...prev, total: Number(count) }));
        setDataSource(list);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  // antd Table onChange signature; also reused by grid/list via onPageChange.
  const tableOnChange = async (
    newPagination: any,
    _filters?: any,
    sorter: any = {}
  ) => {
    const apiParams: any = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter?.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // page size changed — recompute the equivalent page
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1;
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId
            : dataSource[0]?.paginationId;
        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData(apiParams);
  };

  // For antd <Pagination> used by grid/list views.
  const onPageChange = (page: number, pageSize: number) =>
    tableOnChange({ current: page, pageSize });

  const refreshListView = () => {
    setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    fetchData({
      oldPage: 1,
      current: "1",
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    });
  };

  const onSearch = async (value: string) => {
    setSearchQuery(value as any);
    setPagination((prev) => ({ ...prev, current: 1 }));
    await fetchData({
      oldPage: 1,
      current: "1",
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery: value,
    });
  };

  // Refetch when the status tab or drawer filters change (covers initial mount).
  useEffect(() => {
    refreshListView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab, filteredData]);

  return {
    dataSource,
    pagination,
    loading,
    searchQuery,
    tableOnChange,
    onPageChange,
    onSearch,
    refreshListView,
  };
};
